import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { SlideId } from "../types";

const STATIC_DISPLAY_MS = 10_000;
const TRANSITION_MS = 700;

export interface RenderedSlide {
  readonly id: SlideId;
  readonly label: string;
  readonly content: ReactNode;
}

interface SlideshowProps {
  readonly slides: readonly RenderedSlide[];
  readonly paused: boolean;
  readonly onActiveSlideChange: (slideId: SlideId) => void;
}

export function Slideshow({ slides, paused, onActiveSlideChange }: SlideshowProps) {
  const [activeId, setActiveId] = useState<SlideId>(() => slides[0]?.id ?? "program");
  const [outgoingId, setOutgoingId] = useState<SlideId | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const displayDeadlineRef = useRef<number | null>(null);
  const onActiveSlideChangeRef = useRef(onActiveSlideChange);
  onActiveSlideChangeRef.current = onActiveSlideChange;
  const slideIdsKey = slides.map((slide) => slide.id).join("|");
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const activeIndex = useMemo(
    () => slides.findIndex((slide) => slide.id === activeId),
    [activeId, slides],
  );
  const activeSlide = slides[activeIndex >= 0 ? activeIndex : 0];
  const outgoingSlide = slides.find((slide) => slide.id === outgoingId);

  useEffect(() => {
    const currentSlides = slidesRef.current;
    if (currentSlides.length === 0) {
      displayDeadlineRef.current = null;
      setOutgoingId(null);
      setTransitioning(false);
      return;
    }
    if (currentSlides.some((slide) => slide.id === activeId)) return;
    const firstId = currentSlides[0]?.id;
    if (!firstId) return;
    displayDeadlineRef.current = null;
    setActiveId(firstId);
    setOutgoingId(null);
    setTransitioning(false);
  }, [activeId, slideIdsKey]);

  useEffect(() => {
    if (!activeSlide) return;
    onActiveSlideChangeRef.current(activeSlide.id);
  }, [activeSlide?.id]);

  useEffect(() => {
    if (!transitioning) return;
    const transitionId = window.setTimeout(() => {
      setOutgoingId(null);
      setTransitioning(false);
      displayDeadlineRef.current = null;
    }, TRANSITION_MS);
    return () => window.clearTimeout(transitionId);
  }, [transitioning]);

  useEffect(() => {
    const currentSlides = slidesRef.current;
    if (paused || transitioning || activeIndex < 0) {
      if (paused || transitioning) displayDeadlineRef.current = null;
      return;
    }
    const now = performance.now();
    const deadline = displayDeadlineRef.current ?? now + STATIC_DISPLAY_MS;
    displayDeadlineRef.current = deadline;
    if (currentSlides.length <= 1) return;
    const displayId = window.setTimeout(() => {
      const latestSlides = slidesRef.current;
      const currentIndex = latestSlides.findIndex((slide) => slide.id === activeId);
      if (currentIndex < 0 || latestSlides.length <= 1) return;
      const nextIndex = (currentIndex + 1) % latestSlides.length;
      const nextSlide = latestSlides[nextIndex];
      if (!nextSlide || nextSlide.id === activeId) return;
      displayDeadlineRef.current = null;
      setOutgoingId(activeId);
      setActiveId(nextSlide.id);
      setTransitioning(true);
    }, Math.max(0, deadline - now));
    return () => window.clearTimeout(displayId);
  }, [activeId, activeIndex, paused, slideIdsKey, transitioning]);

  return (
    <section
      className="slideshow"
      aria-live="off"
      data-active-slide={activeSlide?.id ?? ""}
    >
      <div className="slide-stage">
        {outgoingSlide ? (
          <div className="slide-layer slide-layer--outgoing" aria-hidden="true">
            {outgoingSlide.content}
          </div>
        ) : null}
        {activeSlide ? (
          <div
            className={`slide-layer ${transitioning ? "slide-layer--incoming" : "slide-layer--current"}`}
            data-testid={`slide-${activeSlide.id}`}
          >
            {activeSlide.content}
          </div>
        ) : null}
      </div>
    </section>
  );
}
